// usePushSubscribe — register/unregister the browser's PushManager
// against our backend, gated by Notification permission and SW availability.
//
// Usage:
//   const { ensureSubscribed, unsubscribe, isSubscribed } = usePushSubscribe();
//   await ensureSubscribed(); // safe to call after user grants permission
//
// The hook is intentionally idempotent: calling ensureSubscribed() multiple
// times is fine; we read the existing subscription if present.

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFlag } from '../context/FeatureFlagsContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export function usePushSubscribe() {
  const { user } = useAuth();
  const enabled = useFlag('system.web_push', true);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Reflect existing subscription state on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const reg = await getRegistration();
      if (!reg) return;
      try {
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setIsSubscribed(!!sub);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const ensureSubscribed = useCallback(async () => {
    if (!enabled) return false;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

    const reg = await getRegistration();
    if (!reg) return false;

    try {
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data } = await axios.get(`${API_URL}/api/push/vapid-public-key`);
        if (!data?.key) return false;
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.key),
        });
      }
      // Always upsert — refreshes last_seen and recovers from backend wipes
      const json = sub.toJSON();
      await axios.post(
        `${API_URL}/api/push/subscribe`,
        { endpoint: json.endpoint, keys: json.keys },
        { withCredentials: true }
      );
      setIsSubscribed(true);
      return true;
    } catch (err) {
      // Most common: NotAllowedError, AbortError when permission revoked mid-flow
      console.warn('Push subscribe failed:', err?.message || err);
      setIsSubscribed(false);
      return false;
    }
  }, [enabled]);

  const unsubscribe = useCallback(async () => {
    const reg = await getRegistration();
    if (!reg) return false;
    try {
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setIsSubscribed(false);
        return true;
      }
      const json = sub.toJSON();
      try {
        await axios.post(
          `${API_URL}/api/push/unsubscribe`,
          { endpoint: json.endpoint, keys: json.keys || {} },
          { withCredentials: true }
        );
      } catch {
        // server-side cleanup best-effort
      }
      await sub.unsubscribe();
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.warn('Push unsubscribe failed:', err?.message || err);
      return false;
    }
  }, []);

  return { ensureSubscribed, unsubscribe, isSubscribed, enabled };
}
